import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, Globe, Clock, Instagram, Youtube, Music2, CheckCircle2 } from "lucide-react";
import { MaskedLine, FadeIn } from "@/components/Reveal";
import { Field, SelectField, Option, SubmitButton } from "@/components/FormBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const facts = [
  { icon: Building2, label: "COMPANY", value: "Aflimart Technologies LTD" },
  { icon: Globe, label: "PRODUCT", value: "Aflimart Digital Market" },
  { icon: Mail, label: "EMAIL", value: "aflimart.tech@gmail.com", href: "mailto:aflimart.tech@gmail.com", testId: "contact-email-link" },
  { icon: Phone, label: "PHONE", value: "+234 703 151 7181", href: "tel:+2347031517181", testId: "contact-phone-link" },
  { icon: Clock, label: "STATUS", value: "Pre-launch — building toward opening day" },
];

const socials = [
  { icon: Instagram, label: "Instagram", handle: "@aflimart_digitalmarket", href: "https://instagram.com/aflimart_digitalmarket", testId: "social-instagram" },
  { icon: Music2, label: "TikTok", handle: "@aflimart.digital", href: "https://tiktok.com/@aflimart.digital", testId: "social-tiktok" },
  { icon: Youtube, label: "YouTube", handle: "@AflimartDigitalMarket", href: "https://youtube.com/@AflimartDigitalMarket", testId: "social-youtube" },
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("send failed");
      form.reset();
      setSent(true);
      toast.success("Message sent.", {
        description: "Thank you — your enquiry is on its way to the Aflimart team.",
      });
    } catch (err) {
      toast.error("Could not send your message.", {
        description: "Please try again, or email aflimart.tech@gmail.com directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-page" className="relative overflow-hidden pb-24 pt-32 sm:pt-40 lg:pt-48">
      <div className="pointer-events-none absolute -top-24 left-[-8%] h-[26rem] w-[26rem] rounded-full bg-[#1E3A8A]/10 blur-[110px]" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
        <div>
          <p className="eyebrow" data-testid="contact-eyebrow">Contact</p>
          <h1 className="mt-8 font-sans text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Start a</MaskedLine>
            <MaskedLine delay={0.3} className="text-gold-gradient">conversation.</MaskedLine>
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-[#64748B] sm:text-lg">
              Questions about early access, partnerships or anything else — reach us directly by
              email or phone, or send a note using the form. Aflimart Digital Market is a product of
              Aflimart Technologies LTD.
            </p>
            <div className="mt-12 space-y-6">
              {facts.map((f, i) => (
                <FadeIn key={f.label} delay={0.1 * i}>
                  <div data-testid={`contact-fact-${i + 1}`} className="flex items-center gap-4 border-l border-[#1E3A8A]/25 pl-5">
                    <f.icon size={18} className="shrink-0 text-[#1E3A8A]" />
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.3em] text-[#94A3B8]">{f.label}</p>
                      {f.href ? (
                        <a href={f.href} data-testid={f.testId} className="mt-1 block text-sm text-[#475569] transition-colors duration-300 hover:text-[#1E3A8A] sm:text-base">
                          {f.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-sm text-[#475569] sm:text-base">{f.value}</p>
                      )}
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.35}>
              <p className="field-label mt-12">Follow Aflimart</p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="contact-social-links">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.testId}
                    className="inline-flex items-center gap-2.5 rounded-full border border-[#1E3A8A]/25 px-5 py-2.5 text-sm text-[#475569] transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#1E3A8A]/60 hover:text-[#1E3A8A]"
                  >
                    <s.icon size={15} className="text-[#1E3A8A]" />
                    <span>{s.handle}</span>
                  </a>
                ))}
              </div>
            </FadeIn>
          </FadeIn>
        </div>

        <FadeIn delay={0.35}>
          {sent ? (
            <div data-testid="contact-success" className="card-dark p-8 text-center sm:p-10 hover:transform-none">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10">
                <CheckCircle2 size={26} className="text-[#1E3A8A]" />
              </span>
              <h2 className="mt-6 font-sans text-2xl text-[#0F172A]">Message sent.</h2>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#64748B]">
                Thank you for reaching out — your enquiry has been delivered to the Aflimart team.
                We will reply to your email address as soon as we can.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="contact-send-another-button"
                className="btn-ghost mt-8 !px-6 !py-3 !text-xs"
              >
                Send another message
              </button>
            </div>
          ) : (
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="card-dark p-8 sm:p-10 hover:transform-none"
          >
            <h2 className="font-sans text-2xl text-[#0F172A]">Send a message</h2>
            <p className="mt-2 text-sm text-[#94A3B8]">Your message goes straight to the Aflimart team inbox.</p>
            <div className="mt-8 space-y-7">
              <Field label="Your name" name="name" testId="contact-name-input" required placeholder="Full name" />
              <Field label="Email address" name="email" type="email" testId="contact-email-input" required placeholder="you@example.com" />
              <SelectField label="Subject" name="subject" testId="contact-subject-select">
                <Option value="">Choose a topic</Option>
                <Option value="general">General inquiry</Option>
                <Option value="buyer">Buyer early access</Option>
                <Option value="vendor">Vendor & partnerships</Option>
                <Option value="other">Other</Option>
              </SelectField>
              <Field label="Message" name="message" testId="contact-message-input" textarea required placeholder="How can we help?" />
              <SubmitButton loading={loading} testId="contact-submit-button">
                Send Message
              </SubmitButton>
            </div>
          </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
}
